'use client';

import React from 'react';
import Link from 'next/link';
import { Container } from '@mantine/core';
import styles from './GiftCardBanner.module.scss';
import { useAOS } from '@/src/lib/hooks/useAOS';

export type GiftCardBannerSlice = {
    type: 'gift-card-banner';

    tone?: 'kids' | 'serious';
    showSeparator?: boolean;

    title: string;
    paragraph: string;

    ctaLabel: string;
    ctaUrl: string;

    /** Optional background image (fixed-like feel but clipped to section) */
    backgroundImageUrl?: string;

    /** Optional sticker text (small playful overlay) */
    stickerText?: string;

    aos?: {
        animation?: string;
        delay?: number;
    };
};

export default function GiftCardBanner({ slice }: { slice: GiftCardBannerSlice }) {
    useAOS();

    const {
        tone = 'kids',
        showSeparator = true,
        title,
        paragraph,
        ctaLabel,
        ctaUrl,
        backgroundImageUrl,
        stickerText = '🎁 Carte cadeau',
        aos,
    } = slice;

    return (
        <section
            className={[
                styles.section,
                tone === 'kids' ? styles.kids : styles.serious,
                showSeparator ? styles.withSeparator : styles.noSeparator,
            ].join(' ')}
            data-aos={aos?.animation ?? 'fade-up'}
            data-aos-delay={typeof aos?.delay === 'number' ? aos.delay : undefined}
            aria-label="Gift card banner"
        >
            {/* Background (component-only) */}
            {backgroundImageUrl && (
                <div className={styles.bg} aria-hidden="true">
                    <div className={styles.bgImage} style={{ backgroundImage: `url(${backgroundImageUrl})` }} />
                    <div className={styles.bgOverlay} />
                </div>
            )}

            <Container size="xl" className={styles.container}>
                <div className={styles.banner}>
                    {/* Asymmetric “ribbon” block */}
                    <div className={styles.ribbon} aria-hidden="true" />

                    {/* Main content card */}
                    <div className={styles.card}>
                        <span className={styles.sticker} aria-hidden="true">
                            {stickerText}
                        </span>

                        <h2 className={styles.title}>{title}</h2>
                        <p className={styles.paragraph}>{paragraph}</p>

                        <Link href={ctaUrl} className={styles.cta}>
                            {ctaLabel} <span aria-hidden="true">→</span>
                        </Link>
                    </div>

                    {/* Floating accent “ticket” */}
                    <div className={styles.ticket} aria-hidden="true">
                        <div className={styles.ticketInner}>
                            <span className={styles.ticketTop}>MiniMonkeys</span>
                            <span className={styles.ticketBottom}>Gift Card</span>
                        </div>
                    </div>
                </div>
            </Container>
        </section>
    );
}
